import { toast } from "react-toastify";
import { all, call, fork, put, takeLatest, select } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";
import {
  FETCH_USER_EXISTING_COURSE,
  USER_LOGIN,
} from "../../constants/ActionTypes";
import Api from "../../util/Api";
import { encryptData } from "../../util/crypto";
import {
  fetchUserExistingCourseInfo,
  showUserLoader,
  userLoginInfo,
  userProfileInfo,
} from "../actions";

function* userLoginApi(action) {

  try {
    yield put(showUserLoader(true));
    const formData = new FormData();
    const session_id = uuidv4();

    // Get directAccess from Redux state
    const directAccess = yield select(state => state.wizardLaunch.directAccess);
    const isDirectCourseAccess = directAccess?.data?.question_type === "directcourseaccess";

    const payload = {
      email_id: action.payload.email,
      password: encryptData(action.payload.password),
      loginStatus: "signin",
      auth_code: process.env.REACT_APP_CDL_SHOP_API_KEY,
      session_id: encryptData(session_id),
    };

    formData.append("sentFormData", JSON.stringify(payload));

    const response = yield call(Api.post, "/ws_log_in", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const parseResponse = JSON.parse(response.data.data_key);
    parseResponse.session_id = session_id;
    const profileFormData = new FormData();
    const profilePayload = { user_id: parseResponse.userId };
    profileFormData.append("sentFormData", JSON.stringify(profilePayload));
    const profileResponse = yield call(
      Api.post,
      "/ws_get_user_profile",
      profileFormData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${parseResponse.jwt}`,
        },
      }
    );
    const profile = profileResponse?.data?.profile;

    // Convert DOB from YYYY-MM-DD to MM-DD-YYYY
    if (profile.dob) {
      const [yyyy, mm, dd] = profile.dob.split('-');
      profile.dob = `${mm}-${dd}-${yyyy}`;
    }
    
    const courseFormData = new FormData();
    const coursePayload = {
      user_id: parseResponse.userId,
      session_id: encryptData(session_id),
    };
    courseFormData.append("sentFormData", JSON.stringify(coursePayload));
    const courseResponse = yield call(
      Api.post,
      "/ws_course_details",
      courseFormData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${parseResponse.jwt}`,
        },
      }
    );
    // Modify the section ID comparison based on directAccess
    const purchasedSectionIds =
      courseResponse?.data?.purchased_course?.section_data
        ?.filter((course) => Number(course.no_of_days) > 0)
        .map((course) => isDirectCourseAccess ? course.new_section_id : course.section_id) || [];

    const mappedCourses = action.courseInfo.map((course) => ({
      ...course,
      alreadyPurchased: purchasedSectionIds.includes(course.section_id),
    }));

    const endorsementsCourses = action.endorsementCourseInfo.map((course) => ({
      ...course,
      alreadyPurchased: purchasedSectionIds.includes(course.section_id),
    }));

    const directPurchasedCourses = action.directCoursesInfo.map((course) => ({
      ...course,
      alreadyPurchased: purchasedSectionIds.includes(course.section_id),
    }));

    const isAlreadyPurchased = [...mappedCourses, ...endorsementsCourses, ...directPurchasedCourses].some(
      (course) => course?.alreadyPurchased
    );
    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }
    toast.success("User Login Successfully");
    yield put(userLoginInfo({...parseResponse, session_id: session_id}));
    yield put(userProfileInfo(profile));
    yield put(fetchUserExistingCourseInfo(courseResponse?.data));
    if (isAlreadyPurchased) action.navigate("/shoppingcart");
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while Login. Please try again.";
    console.error(message);
    toast.error(message);
  } finally {
    yield put(showUserLoader(false));
  }
}
function* fetchUserExistingCourseApi(action) {
  try {
    const formData = new FormData();
    const payload = {
      user_id: action.payload.userId,
      session_id: encryptData(action.payload.session_id),
    };
    formData.append("sentFormData", JSON.stringify(payload));
    const response = yield call(Api.post, "/ws_course_details", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${action.payload.jwt}`,
      },
    });
    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }
    yield put(fetchUserExistingCourseInfo(response.data));
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while Login. Please try again.";
    console.error(message);
    toast.error(message);
  }
}

function* UserLoginSaga() {
  yield takeLatest(USER_LOGIN, userLoginApi);
}

function* UserCourseInfoSaga() {
  yield takeLatest(FETCH_USER_EXISTING_COURSE, fetchUserExistingCourseApi);
}

export default function* rootSaga() {
  yield all([fork(UserLoginSaga), fork(UserCourseInfoSaga)]);
}